import React, { useMemo, lazy } from 'react';
import DataSetCardOverviewTabContainer from 'js/components/data_sets/cards/DataSetCardOverviewTabContainer';
import DataSetDataBrowserContainer from 'js/components/data_sets/cards/DataSetDataBrowserContainer';
import DataSetRecipeTab from 'js/components/data_sets/cards/DataSetRecipeTab';
import AclTabViewContainer from 'js/components/elements/aclView/AclTabViewContainer';
import { ACL_RESOURCE_TYPE_DF_DATASET } from 'js/constants/aclConstants';
import { NexsetStatsTab } from 'new/elements/support/detailTabs/NexsetStatsTab';
import { ResourceNotifications } from 'new/elements/support/detailTabs/ResourceNotifications';
import { Slide } from '@mui/material';
import { StyledContainer } from 'js/components/DataListContainer';
import { getSourceMeta } from 'new/hooks/adapters/useMySources';
import { has, isNil } from 'lodash';
import { connect, useSelector } from 'react-redux';
import usePaging from 'new/state/paging/repository/usePaging';
import useEntities from 'new/state/entities/repository/useEntities';
import { DataSetDetailHeader } from 'js/components/data_sets/cards/DataSetDetailHeader';
import DateUtils from 'js/lib/DateUtils';
import UIUtils from 'js/lib/UIUtils';
import { useListOldReduxContainer } from '../DataCard/useListOldReduxContainer';
import { DATASET } from 'js/constants/notificationsTypes';
import { RESOURCE_TYPE_DATA_SET } from 'js/constants/resourceTypes';
import { isDetectedDataset, getConnectionOrVendorType } from 'js/lib/ResourceUtils';
import styles from './NexsetDetailDrawer.module.scss';
import { getDataSetEntities } from 'js/reducers/entities';
import { getAllowedNexsetTabs, NEXSET_TAB_IDS, TAB_METADATA } from 'new/utils/nexsetTabs';

const CardDetailView = lazy(() => import('js/components/elements/cards/CardDetailView'));
const ResourceActivity = lazy(() => import('new/elements/support/detailTabs/ResourceActivity'));
const ResourceErrorData = lazy(() => import('new/elements/support/detailTabs/ResourceErrorData'));

interface Props {
  nexsetId: string;
  data: any[];
  getSourceMetaFromDataSet: (dataSetId: string, isDetected: boolean) => any;
  onClose: () => void;
}

/**
 * Component mapping for nexset tabs in NexsetDetailDrawer format.
 * Maps tab IDs to React components with their props.
 */
const getNexsetTabComponentForDrawer = (tabId: string) => {
  switch (tabId) {
    case NEXSET_TAB_IDS.OVERVIEW:
      return <DataSetCardOverviewTabContainer />;

    case NEXSET_TAB_IDS.SCHEMA:
      return <DataSetDataBrowserContainer />;

    case NEXSET_TAB_IDS.RULES:
      return <DataSetRecipeTab />;

    case NEXSET_TAB_IDS.STATS:
      // @ts-ignore Ignoring the missing "data" prop error because that prop is
      // passed by CardDetailView
      return <NexsetStatsTab />;

    case NEXSET_TAB_IDS.NOTIFICATIONS:
      // @ts-ignore
      return <ResourceNotifications notificationType={DATASET} />;

    case NEXSET_TAB_IDS.ACTIVITY:
      // @ts-ignore
      return <ResourceActivity resourceType={RESOURCE_TYPE_DATA_SET} />;

    case NEXSET_TAB_IDS.ERROR_DATA:
      // @ts-ignore
      return <ResourceErrorData resourceType={RESOURCE_TYPE_DATA_SET} />;

    case NEXSET_TAB_IDS.SHARE:
      return <AclTabViewContainer resourceType={ACL_RESOURCE_TYPE_DF_DATASET} />;

    default:
      return null;
  }
};

const NexsetDetailDrawer = ({ data, nexsetId, getSourceMetaFromDataSet, onClose }: Props) => {
  const publicDatasets = useSelector((state: any) => state.publicDatasets);
  const sharedDatasets = useSelector((state: any) => state.sharedDatasets);
  const { aclQueryMode } = useSelector((state: any) => state.app.listing);

  const getData = useListOldReduxContainer(data, mapStateToProps, (ownProps) => {
    const {
      cardData,
      readOnly,
      adminAccess,
      aclQueryMode,
      isManaged,
      owner,
      summaryData,
      isDetected,
      parentDataSet,
      source,
      connectionOrVendorType,
    } = ownProps;
    if (!cardData) return null;
    const createdAt = DateUtils.convertDateStringToFormat(cardData.created_at);
    const name = cardData.name || '';
    const description = cardData.description || ' ';
    const status = cardData.status;
    const shared =
      publicDatasets.byId.includes(cardData.id) || sharedDatasets.byId.includes(cardData.id);

    let parentDataSetName = null,
      sourceIcon = null,
      sourceName = null,
      dataSetSourceName = null;

    if (isDetected === true) {
      const connectionInfo = getSourceMeta(cardData.id, isDetected);
      dataSetSourceName = source ? source.name : null;

      if (connectionInfo) {
        sourceIcon = connectionInfo.iconSm;
        sourceName = connectionInfo.name;
      } else {
        sourceName = connectionOrVendorType ? connectionOrVendorType : '';
      }
    } else {
      parentDataSetName = parentDataSet ? parentDataSet.name : '';
    }

    let statsContentView = null;
    if (summaryData) {
      let derivedDataSetsCount = has(summaryData, 'data_sets.total')
        ? summaryData.data_sets.total
        : 0;
      let derivedDataSetsLabel = UIUtils.toPluralFormat(
        'Transformed Nexset',
        derivedDataSetsCount,
        false,
      );
      let sinksCount = has(cardData, 'data_sinks') ? cardData.data_sinks.length : 0;
      let sinksLabel = UIUtils.toPluralFormat('Destination', sinksCount, false);
      let sharersCount = UIUtils.getDatasetSharersCount(cardData, summaryData);
      let sharersLabel = UIUtils.toPluralFormat('Share', sharersCount, false);
      if (!shared) {
        statsContentView = (
          <div className="counter-holder">
            <span className="counter">{derivedDataSetsCount} </span>
            <span className="counter-label">{derivedDataSetsLabel}</span>
            <span className="counter-label">{', '}</span>
            <span className="counter">{sinksCount} </span>
            <span className="counter-label">{sinksLabel}</span>
            <span className="counter-label">{', '}</span>
            <span className="counter">{sharersCount} </span>
            <span className="counter-label">{sharersLabel}</span>
          </div>
        );
      }
    }

    return {
      cardData,
      ...cardData,
      name,
      description,
      createdAt,
      owner,
      readOnly,
      adminAccess,
      isManaged,
      aclQueryMode,
      shared,
      isDetected,
      status,
      statsContentView,
      parentDataSetName,
      sourceIcon,
      sourceName,
      dataSetSourceName,
    };
  });

  const dataSetIndex = useMemo(
    () => data.findIndex((item: any) => item.id === nexsetId),
    [data, nexsetId],
  );

  const dataSetDetailHeaderData = useMemo(() => getData(dataSetIndex), [getData, dataSetIndex]);

  const { getMetaInfoById } = usePaging();
  const { data: sources } = useEntities('sources');

  const tabs = useMemo(() => {
    if (!dataSetDetailHeaderData) {
      return [];
    }

    const { cardData, source, parentDataSet } = dataSetDetailHeaderData;
    if (!cardData) {
      return [];
    }

    const flowType = cardData?.flow_type;
    const dataSource = sources[cardData?.data_source] || source;

    const allowedTabIds = getAllowedNexsetTabs({
      data: cardData,
      flowType,
      getMetaInfoById,
      dataSource,
      parentDataSet,
    });

    return allowedTabIds.map((tabId) => {
      const metadata = TAB_METADATA[tabId];
      return {
        tabName: metadata.label,
        tabElement: getNexsetTabComponentForDrawer(tabId),
      };
    });
  }, [dataSetDetailHeaderData, getMetaInfoById, sources]);

  return (
    <div className={styles.container}>
      <div className="modal-fader" onClick={onClose} />
      <Slide direction="left" in>
        <StyledContainer disableGutters>
          <React.Suspense fallback={<div>loading...</div>}>
            <CardDetailView
              data={dataSetDetailHeaderData}
              tabs={tabs}
              noHeaderPadding
              detailHeaderContent={
                <DataSetDetailHeader
                  disableActions
                  onCloseDrawer={onClose}
                  {...dataSetDetailHeaderData}
                />
              }
              getSourceMeta={getSourceMetaFromDataSet}
              aclQueryMode={aclQueryMode}
            />
          </React.Suspense>
        </StyledContainer>
      </Slide>
    </div>
  );
};

const memoizedNexsetDetailDrawer = React.memo(NexsetDetailDrawer);

const mapStateToProps = (state, ownProps) => {
  const getSourceMetaFromDataSet = (dataSetId, isDetected) => {
    const {
      entities: { dataSets, sources, vendors },
      paging: { metaInfoById },
    } = state;
    const dataSet = dataSets[dataSetId];
    if (isDetected === false) {
      return null;
    }
    if (isNil(isDetected) && isDetectedDataset(dataSet) === false) {
      return null;
    }

    const sourceId = dataSet.data_source;
    const sourceEntity = sources[sourceId];

    const connectionOrVendorType = getConnectionOrVendorType(
      sourceEntity,
      vendors,
      'source_type',
      'vendor_id',
    );

    return connectionOrVendorType && has(metaInfoById, connectionOrVendorType)
      ? metaInfoById[connectionOrVendorType]
      : null;
  };

  return {
    data: Object.values(getDataSetEntities(state)),
    getSourceMetaFromDataSet,
    ...ownProps,
  };
};

export default connect(mapStateToProps)(memoizedNexsetDetailDrawer);
